import React from 'react';

import { PestOption } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';

/**
 * Sort pest objects alphabetically by name
 */
export const createPersistentPestOptions = (
  pestObjects: PestOption[]
): PestOption[] => {
  return [...pestObjects].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );
};

/**
 * Filter and format search pest results
 */
export const createSearchPestOptions = (
  pestsData: PestOption[]
): PestOption[] => {
  return (pestsData || [])
    .filter(p => p?.name && typeof p.name === 'string' && p.name.trim() !== '')
    .map(p => ({
      id: p.id,
      pestId: p.pestId,
      nameId: p.nameId,
      name: p.name.trim(),
      language: p.language || 'en'
    }));
};

/**
 * Get IDs for selected pests from current pest options
 */
export const getIdsForSelectedPests = (
  pestOptions: PestOption[],
  tempSelectedPests: PestOption[]
): string[] => {
  if (pestOptions.length === 0 || tempSelectedPests.length === 0) {
    return [];
  }

  return pestOptions
    .filter(pestOption =>
      tempSelectedPests.some(
        tempSelectedPest => tempSelectedPest.id === pestOption.id
      )
    )
    .map(pest => pest.id);
};

/**
 * Check if two arrays are different
 */
export const areArraysDifferent = (arr1: string[], arr2: string[]): boolean => {
  return arr1.length !== arr2.length || !arr1.every(id => arr2.includes(id));
};

/**
 * Check if pest is already in persistent list
 */
export const isPestInPersistentList = (
  pest: PestOption,
  persistentList: PestOption[]
): boolean => {
  return persistentList.some(p => p.id === pest.id);
};

/**
 * Check if a persistent pest should be kept based on current selection
 */
export const shouldKeepPersistentPest = (
  persistentPest: PestOption,
  tempSelectedPests: PestOption[]
): boolean => {
  return tempSelectedPests.some(
    tempSelectedPest => tempSelectedPest.id === persistentPest.id
  );
};

/**
 * Focus search input with fallback
 */
export const focusSearchInput = (
  searchInputRef: React.RefObject<any>,
  delay = 100
): ReturnType<typeof setTimeout> => {
  return setTimeout(() => {
    if (searchInputRef.current?.focus) {
      searchInputRef.current.focus();
      return;
    }
    // The timer can outlive the component (and jsdom teardown in tests), so
    // bail if there's no document instead of throwing a ReferenceError.
    if (typeof document === 'undefined') {
      return;
    }
    const inputElement = document.getElementById('pest-search');
    if (inputElement?.focus) {
      inputElement.focus();
    }
  }, delay);
};
